import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import {
  GET_INVOICE_AGING_PLACEMENTS,
  InvoiceAgingFilter,
  InvoiceAgingPlacementRow,
  InvoiceAgingQueryData,
} from './gql';
import { AllBuckets, applyBucketFilter, computeKpis, groupByCompany } from './utils/derive';
import { StatusKey, StatusChips } from './components/FilterRow';
import KpiStrip from './components/KpiStrip';
import FilterRow from './components/FilterRow';
import ResultToolbar from './components/ResultToolbar';
import CompanyGroupCard from './components/CompanyGroupCard';
import Loader from '../../../components/layout/Loader';
import NoDataFound from '../../../components/layout/NoDataFound';
import './invoiceAging.scss';

// Sent as `filters.statuses` to the API, which filters the placementStatus
// column by its ENUM VALUE (what the DB stores), not the enum key.
const STATUS_TO_GQL: Record<StatusKey, string> = {
  'Accounting Approved': 'Accounting Approved',
  'Accounting Invoiced': 'Accounting Invoiced',
  Paid: 'Paid',
};

const DEFAULT_STATUSES: StatusChips = {
  'Accounting Approved': true,
  'Accounting Invoiced': true,
  Paid: false,
};

const DEBOUNCE_MS = 600;

// Get today normalized for stable reference (production uses real today)
function getToday(): Date {
  return new Date();
}

const InvoiceAging = () => {
  const today = useRef<Date>(getToday());

  // Text filter state (displayed immediately, debounced before refetch)
  const [companySearch, setCompanySearch] = useState('');
  const [jobTitleSearch, setJobTitleSearch] = useState('');
  const [candidateSearch, setCandidateSearch] = useState('');

  // Status chips — default: Approved + Invoiced ON, Paid OFF
  const [statuses, setStatuses] = useState<StatusChips>(DEFAULT_STATUSES);

  // Client-only state
  const [bucket, setBucket] = useState<AllBuckets>('all');
  const [openMap, setOpenMap] = useState<Record<string, boolean>>({});

  const [fetchPlacements, { data, loading }] = useLazyQuery<InvoiceAgingQueryData>(
    GET_INVOICE_AGING_PLACEMENTS,
    { fetchPolicy: 'cache-and-network' }
  );

  // Debounce timer ref
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const doFetch = useCallback(
    (
      cs: string,
      js: string,
      cands: string,
      chips: StatusChips
    ) => {
      const selectedStatuses = (Object.keys(chips) as StatusKey[])
        .filter((k) => chips[k])
        .map((k) => STATUS_TO_GQL[k]);

      const filters: InvoiceAgingFilter = {};
      if (cs.trim()) filters.companySearch = cs.trim();
      if (js.trim()) filters.jobTitleSearch = js.trim();
      if (cands.trim()) filters.candidateSearch = cands.trim();
      if (selectedStatuses.length) filters.statuses = selectedStatuses;

      fetchPlacements({ variables: { filters } });
    },
    [fetchPlacements]
  );

  // Debounced refetch on text changes
  const scheduleRefetch = useCallback(
    (cs: string, js: string, cands: string, chips: StatusChips) => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(() => {
        doFetch(cs, js, cands, chips);
      }, DEBOUNCE_MS);
    },
    [doFetch]
  );

  // Initial fetch on mount
  useEffect(() => {
    doFetch(companySearch, jobTitleSearch, candidateSearch, statuses);
    document.title = 'Aged Invoices - RealREPP';
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handlers
  const handleCompanySearch = (v: string) => {
    setCompanySearch(v);
    scheduleRefetch(v, jobTitleSearch, candidateSearch, statuses);
  };

  const handleJobTitleSearch = (v: string) => {
    setJobTitleSearch(v);
    scheduleRefetch(companySearch, v, candidateSearch, statuses);
  };

  const handleCandidateSearch = (v: string) => {
    setCandidateSearch(v);
    scheduleRefetch(companySearch, jobTitleSearch, v, statuses);
  };

  const handleToggleStatus = (key: StatusKey) => {
    const next: StatusChips = { ...statuses, [key]: !statuses[key] };
    setStatuses(next);
    // Status toggles refetch immediately (no debounce needed — chip click, not typing)
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    doFetch(companySearch, jobTitleSearch, candidateSearch, next);
  };

  const handleBucketChange = (b: AllBuckets) => {
    setBucket(b);
    // Force all companies open when a bucket is selected
    if (b !== 'all') {
      const allRows = data?.invoiceAgingPlacements?.rows ?? [];
      const newMap: Record<string, boolean> = {};
      allRows.forEach((r) => {
        if (r.companyId) newMap[r.companyId] = true;
      });
      setOpenMap(newMap);
    }
  };

  const handleExpandAll = () => {
    const allRows = data?.invoiceAgingPlacements?.rows ?? [];
    const newMap: Record<string, boolean> = {};
    allRows.forEach((r) => {
      if (r.companyId) newMap[r.companyId] = true;
    });
    setOpenMap(newMap);
  };

  const handleCollapseAll = () => {
    setOpenMap({});
  };

  const handleToggleCompany = (companyId: string) => {
    // When a specific bucket is active, individual toggle still works
    setOpenMap((prev) => ({ ...prev, [companyId]: !prev[companyId] }));
  };

  // Derived data
  const allRows: InvoiceAgingPlacementRow[] = data?.invoiceAgingPlacements?.rows ?? [];
  const capped = data?.invoiceAgingPlacements?.capped ?? false;
  const cap = data?.invoiceAgingPlacements?.cap ?? 2000;

  const filteredRows = useMemo(
    () => applyBucketFilter(allRows, bucket, today.current),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allRows, bucket]
  );

  const kpis = useMemo(
    () => computeKpis(filteredRows, today.current),
    [filteredRows]
  );

  const groups = useMemo(
    () => groupByCompany(filteredRows, today.current),
    [filteredRows]
  );

  // Effective open state: bucket ≠ 'all' forces all open
  const effectiveOpenMap: Record<string, boolean> = useMemo(
    () =>
      bucket !== 'all'
        ? Object.fromEntries(groups.map((g) => [g.companyId, true]))
        : openMap,
    [bucket, groups, openMap]
  );

  const hasData = groups.length > 0;

  return (
    <div className="invoice-aging-page admin-tabs team-tabs">
      <div className="primaryHeading mb-0 page-header">
        <h5 className="m-0">Aged Invoices</h5>
      </div>

      {capped && (
        <div className="ia-capped-banner">
          <span className="ia-capped-icon">⚠</span>
          Results are limited to the most recent {cap} placements. Refine your filters to narrow the view.
        </div>
      )}

      <KpiStrip kpis={kpis} />

      <FilterRow
        companySearch={companySearch}
        jobTitleSearch={jobTitleSearch}
        candidateSearch={candidateSearch}
        statuses={statuses}
        onCompanySearch={handleCompanySearch}
        onJobTitleSearch={handleJobTitleSearch}
        onCandidateSearch={handleCandidateSearch}
        onToggleStatus={handleToggleStatus}
      />

      <ResultToolbar
        count={filteredRows.length}
        bucket={bucket}
        onBucketChange={handleBucketChange}
        onExpandAll={handleExpandAll}
        onCollapseAll={handleCollapseAll}
      />

      {loading && (
        <div className="ia-loader-wrap">
          <Loader loaderClass="sm-height" />
        </div>
      )}

      {!loading && !hasData && (
        <div className="ia-empty-wrap">
          <NoDataFound text="No placements found matching your filters." />
        </div>
      )}

      {!loading && hasData && (
        <div className="ia-company-list">
          {groups.map((group) => (
            <CompanyGroupCard
              key={group.companyId}
              group={group}
              isOpen={effectiveOpenMap[group.companyId] ?? false}
              onToggle={() => handleToggleCompany(group.companyId)}
              today={today.current}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default InvoiceAging;
